import React, { useState } from 'react';
import { DB } from '../../data/database';
import { CheckIcon, CarIcon } from '../Icons';
import { CUT_SOURCE_CATALOG } from '../../data/constants';
import PartsSelector from '../PartsSelector';
import { logActivity, Notifications } from '../../data/notifications';

const CUT_TYPES_MAP = { 'half-cut': 'Half Cut', 'nose-cut': 'Nose Cut', 'ckd': 'CKD', 'skd': 'SKD', 'roof-cut': 'Roof Cut' };

function PartsRequestModal({ vehicle, user, onClose, onSave }) {
  const [step, setStep] = useState(1);
  const [selectedParts, setSelectedParts] = useState([]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  const name = `${vehicle.make || ''} ${vehicle.model || ''}`.trim() || 'Vehicle';
  const vehicleInfo = `${vehicle.year || ''} ${name} — ${vehicle.sku || ''} — ${vehicle.chassis || ''}`.trim();

  // FIX 5: Check for duplicate requests
  const existingRequest = (DB.get('requests') || []).find(r =>
    r.customerId === user.id &&
    r.vehicleId === vehicle.id &&
    ['pending', 'quoted', 'processing'].includes(r.status)
  );

  const handlePartsConfirm = (parts) => {
    setSelectedParts(parts);
    setStep(2);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedParts.length === 0) return setError('Please select at least one part.');

    // Build requested_parts JSONB
    const requestedParts = {
      source_vehicle_id: vehicle.id,
      source_vehicle_info: vehicleInfo,
      parts: selectedParts.map(p => ({
        part_name: p.part_name,
        category: p.category,
        quantity: p.quantity || 1,
        catalog_id: p.catalog_id || null,
        status: 'pending',
        price_quoted: null,
        admin_notes: '',
      })),
    };

    const requests = DB.get('requests') || [];
    const newReq = {
      id: DB.genId('REQ'),
      customerId: user.id,
      customerName: user.name,
      type: 'parts_from_vehicle',
      vehicleId: vehicle.id,
      vehicleSku: vehicle.sku,
      vehicleName: name,
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      chassis: vehicle.chassis || '',
      // Store parts properly
      requested_parts: requestedParts,
      selectedParts: selectedParts.map(p => p.part_name),
      selectedPartsDetail: selectedParts,
      details: notes.trim() || `Parts request from ${name}`,
      status: 'pending',
      createdAt: new Date().toISOString().split('T')[0],
      thread: [],
      timeline: [{
        stage: 'submitted',
        date: new Date().toISOString(),
        note: `${user.name} requested ${selectedParts.length} parts from ${name}`,
        by: 'customer',
        by_name: user.name,
      }],
    };

    DB.set('requests', [...requests, newReq]);

    // Notify admin
    const partsList = selectedParts.map(p => `${p.part_name}${p.quantity > 1 ? ` ×${p.quantity}` : ''}`).join(', ');
    try {
      Notifications.add('request_new', 'Parts Request',
        `${user.name} requested ${selectedParts.length} parts from ${name}: ${partsList}`, {});
    } catch {}

    logActivity.requestNew(user.name, 'Parts from Vehicle');
    onSave?.();
    onClose();
  };

  // FIX 5: Show duplicate warning
  if (existingRequest) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">Existing Request</h3>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-body" style={{ textAlign: 'center', padding: '24px 20px' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
              You already have a pending request for this vehicle
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
              {name} · #{existingRequest.id}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Determine allowed categories based on source type
  const sourceCategory = vehicle.category || (vehicle.cutType ? (CUT_TYPES_MAP[vehicle.cutType] || vehicle.cutType) : null);
  const catalogFilter = CUT_SOURCE_CATALOG[sourceCategory] || CUT_SOURCE_CATALOG['full_car'] || ['all'];
  const allowedCategories = catalogFilter.includes('all') ? null : catalogFilter;

  // Step 1: PartsSelector
  if (step === 1) {
    return (
      <PartsSelector
        mode="request"
        vehicleInfo={vehicle}
        initialSelection={selectedParts}
        allowedCategories={allowedCategories}
        onCancel={onClose}
        onConfirm={handlePartsConfirm}
      />
    );
  }

  // Step 2: Review + notes + submit
  const totalQty = selectedParts.reduce((s, p) => s + (p.quantity || 1), 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Request Parts</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Vehicle info */}
            <div style={{
              display: 'flex', gap: 12, padding: 12,
              background: 'var(--bg3)', borderRadius: 10,
              border: '1px solid var(--border)', marginBottom: 16,
              alignItems: 'center',
            }}>
              {vehicle.images?.length > 0 ? (
                <img src={vehicle.images[0]} alt="" style={{ width: 64, height: 44, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
              ) : (
                <div style={{ width: 64, height: 44, background: 'var(--bg2)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--text3)' }}>
                  <CarIcon />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{name}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                  {vehicle.sku}{vehicle.year ? ` · ${vehicle.year}` : ''}{vehicle.chassis ? ` · ${vehicle.chassis}` : ''}
                </div>
              </div>
            </div>

            {/* Selected parts list */}
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label className="form-label" style={{ margin: 0 }}>
                  Selected Parts ({selectedParts.length} parts, {totalQty} total)
                </label>
                <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => setStep(1)}>
                  Edit
                </button>
              </div>
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                {selectedParts.map((p, i) => (
                  <div key={p.part_name} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 12px', fontSize: 12,
                    borderBottom: i < selectedParts.length - 1 ? '1px solid var(--border)' : 'none',
                  }}>
                    <span>{p.part_name}</span>
                    <span style={{ color: 'var(--text3)' }}>× {p.quantity || 1}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes (optional) */}
            <div className="form-group">
              <label className="form-label">Notes (optional)</label>
              <textarea
                className="form-input"
                rows={2}
                placeholder="Any specific requirements — condition, color, etc."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </div>

            {error && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 4 }}>{error}</div>}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>← Back</button>
            <button type="submit" className="btn btn-primary">
              <CheckIcon /> Submit Request ({selectedParts.length} parts)
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PartsRequestModal;
